"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; } async function _asyncOptionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = await fn(value); } else if (op === 'call' || op === 'optionalCall') { value = await fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }// The "What's New" pill (web, desktop) and Settings row and tab dot (phones,
// narrow web). Each device keeps one marker per user: when they last clicked
// it (openedAt) and when they dismissed it (dismissedAt). The pill shows while
// the newest release is later than dismissedAt, so adding a release brings it
// back. The newest release is the later of the one built into the app and the
// one the website publishes at whatsNewLatestUrl, so a website deploy alone
// brings the pill back; the app reads it on load and on focus at most every
// few hours, and keeps its built-in release when the read fails.

var _changelog = require('shared/data/changelog');

/** unopened: show the pill and tab dot; opened: show the pill with its ×;
 * dismissed: hide both until a later release. */











const storageKey = (userKey) => `bt_whats_new_${userKey}`

const parse = (raw) => {
  try {
    const marker = JSON.parse(raw || 'null')
    return marker && typeof marker === 'object' ? marker : {}
  } catch (e) {
    return {}
  }
}

// A missing or unreadable time is never later than a release.
const isAfter = (time, at) => (at ? Date.parse(at) >= time : false)

const fetchTimeoutMs = 5000
const refreshEveryMs = 3 * 60 * 60 * 1000

/** The newest release's time from the live site; null when it can't be read. */
const fetchLatestTime = async () => {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), fetchTimeoutMs)
  try {
    const response = await fetch(_changelog.whatsNewLatestUrl, { signal: controller.signal })
    const date = response.ok ? await _asyncOptionalChain([(await response.json()), 'optionalAccess', async _ => _.latest, 'optionalAccess', async _2 => _2.date]) : null
    const time = typeof date === 'string' ? _changelog.dateTime.call(void 0, date) : NaN
    return Number.isFinite(time) ? time : null
  } finally {
    clearTimeout(timeout)
  }
}












 const createWhatsNew = (storage, {
  builtInTime = _changelog.newestReleaseTime,
  fetchLatest = fetchLatestTime,
  onFocus,
  now = () => new Date(),
} = {}) => {
  let fetchedTime = null
  let fetchedAt = null
  let isFetching = false
  // An older published release never hides a newer built-in one.
  const newestTime = () => Math.max(builtInTime, _nullishCoalesce(fetchedTime, () => ( builtInTime)))
  // undefined: not read yet; a pending read is tracked separately.
  const markers = new Map()
  const reading = new Set()
  const listeners = new Set()
  const notify = () => listeners.forEach(listener => listener())

  // A failed read keeps the last release this app knew of.
  const refresh = () => {
    if (isFetching || (fetchedAt !== null && now().getTime() - fetchedAt < refreshEveryMs)) return
    isFetching = true
    fetchedAt = now().getTime()
    fetchLatest()
      .catch(() => null)
      .then((time) => {
        isFetching = false
        if (time === null) return
        const before = newestTime()
        fetchedTime = time
        if (newestTime() !== before) notify()
      })
  }

  // Started by the first status a signed-in staff user asks for.
  const start = () => {
    if (fetchedAt !== null) return
    refresh()
    _optionalChain([onFocus, 'optionalCall', _3 => _3(refresh)])
  }

  // Storage that can't be read counts as no marker: the pill shows.
  const read = (userKey) => {
    let raw
    try {
      raw = storage.getItem(storageKey(userKey))
    } catch (e2) {
      raw = null
    }
    if (!(raw instanceof Promise)) {
      markers.set(userKey, parse(raw))
      return
    }
    reading.add(userKey)
    raw
      .catch(() => null)
      .then((value) => {
        reading.delete(userKey)
        if (!markers.has(userKey)) markers.set(userKey, parse(value))
        notify()
      })
  }

  // A clock behind the release (or a release deployed before its date in
  // UTC) still counts as after it, so the click and dismissal take effect.
  const stamp = () => new Date(Math.max(now().getTime(), newestTime())).toISOString()

  const write = (userKey, change) => {
    const marker = { ...markers.get(userKey), ...change }
    markers.set(userKey, marker)
    notify()
    try {
      Promise.resolve(storage.setItem(storageKey(userKey), JSON.stringify(marker))).catch(() => undefined)
    } catch (e3) {
      // Kept for this session only.
    }
  }

  const status = (userKey) => {
    start()
    if (!markers.has(userKey) && !reading.has(userKey)) read(userKey)
    const marker = markers.get(userKey)
    if (!marker) return null
    if (isAfter(newestTime(), marker.dismissedAt)) return 'dismissed'
    return isAfter(newestTime(), marker.openedAt) ? 'opened' : 'unopened'
  }

  return {
    /** null until this device's storage has answered. */
    status,

    /** The person clicked through to the page; the pill may now be dismissed. */
    open: (userKey) => {
      if (status(userKey) === 'unopened') write(userKey, { openedAt: stamp() })
    },

    /** Hides the pill until a later release. Only after it was clicked. */
    dismiss: (userKey) => {
      if (status(userKey) === 'opened') write(userKey, { dismissedAt: stamp() })
    },

    subscribe: (listener) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
  }
}; exports.createWhatsNew = createWhatsNew


